import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  TextField,
  Chip,
  TablePagination,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/store";
import { fetchHistory, clearHistory } from "@/store/historySlice";
import { fetchScrapers } from "@/store/scrapersSlice";
import { useNotification } from "@context/NotificationContext";
import { ConfirmDialog } from "@components/ConfirmDialog";
import { PageHeader } from "@components/PageHeader";

export default function Logs() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const { items, total, loading } = useAppSelector((s) => s.history);
  const scrapers = useAppSelector((s) => s.scrapers.list);
  const [filterScraperId, setFilterScraperId] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    dispatch(fetchScrapers());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchHistory({
        scraperId: filterScraperId || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      }),
    );
  }, [dispatch, filterScraperId, page, rowsPerPage]);

  const handleClear = async () => {
    await dispatch(clearHistory(filterScraperId || undefined));
    notify(t("logs.historyCleared"), "success");
    setConfirmClear(false);
    setPage(0);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <PageHeader sx={{ mb: 0 }}>{t("logs.title")}</PageHeader>
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            select
            size="small"
            label={t("logs.filterByScraper")}
            value={filterScraperId}
            onChange={(e) => {
              setFilterScraperId(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">{t("logs.allScrapers")}</MenuItem>
            {scrapers.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={() => setConfirmClear(true)}
          >
            {t("logs.clearHistory")}
          </Button>
        </Box>
      </Box>
      <Card>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("logs.scraper")}</TableCell>
                  <TableCell>{t("logs.date")}</TableCell>
                  <TableCell align="right">
                    {t("logs.itemsExtracted")}
                  </TableCell>
                  <TableCell align="right">{t("logs.duration")}</TableCell>
                  <TableCell align="center">{t("logs.success")}</TableCell>
                  <TableCell align="center">{t("logs.submitted")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((entry) => (
                  <TableRow key={entry.id} hover>
                    <TableCell>{entry.scraperName}</TableCell>
                    <TableCell>
                      {new Date(entry.date).toLocaleString()}
                    </TableCell>
                    <TableCell align="right">{entry.amount}</TableCell>
                    <TableCell align="right">
                      {entry.duration.toFixed(1)}
                      {t("common.seconds")}
                    </TableCell>
                    <TableCell align="center">
                      {entry.success ? (
                        <CheckCircleIcon fontSize="small" color="success" />
                      ) : (
                        <CancelIcon fontSize="small" color="error" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          entry.submitted ? t("common.yes") : t("common.no")
                        }
                        size="small"
                        color={entry.submitted ? "info" : "default"}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography
                        sx={{
                          color: "text.secondary",
                          py: 4,
                        }}
                      >
                        {t("logs.noLogs")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </CardContent>
      </Card>
      <ConfirmDialog
        open={confirmClear}
        title={t("logs.clearHistory")}
        message={t("logs.confirmClear")}
        onConfirm={handleClear}
        onCancel={() => setConfirmClear(false)}
      />
    </Box>
  );
}
